import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Checkbox,
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { SeverityChip } from 'components/shared/SeverityChip';
import EventStatusBadge from './EventStatusBadge';
import { EmptyState } from 'components/shared/EmptyState';
import { getTimeAgo } from 'components/shared/utils/timeUtils';
import { getComparator, stableSort } from 'libs/sortList';
import { eventsHeadTableCells } from './eventsHeadTableCells';
import type { VMSEvent, EventCounts } from '../../types/canvision.types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TOTAL_COLS = eventsHeadTableCells.length;
const SKELETON_ROW_COUNT = 6;
const INITIAL_VISIBLE_COUNT = 50;
const LOAD_MORE_COUNT = 50;
const SCROLL_THRESHOLD = 100;

// ---------------------------------------------------------------------------
// Memoized row component — prevents re-renders when sibling rows change
// ---------------------------------------------------------------------------

interface EventRowProps {
  event: VMSEvent;
  isSelected: boolean;
  onEventClick: (eventId: number) => void;
  onSelectRow: (eventId: number) => void;
  onRowHover?: (cameraId: number | null) => void;
}

const EventRow = React.memo(function EventRow({
  event,
  isSelected,
  onEventClick,
  onSelectRow,
  onRowHover,
}: EventRowProps) {
  const cameraLabel = event.camera_display_name || event.camera_name || 'Unknown camera';
  const siteLabel = event.site_display_name || event.site_name || '';

  return (
    <TableRow
      hover
      selected={isSelected}
      onClick={() => onEventClick(event.id)}
      onMouseEnter={() => onRowHover?.(event.camera_id ?? null)}
      onMouseLeave={() => onRowHover?.(null)}
      sx={{ cursor: 'pointer' }}
    >
      {/* Checkbox */}
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onSelectRow(event.id)}
          size="small"
          inputProps={{ 'aria-label': `Select event ${event.id}` }}
        />
      </TableCell>

      {/* Camera + Site */}
      <TableCell>
        <Typography variant="body2" noWrap>
          {cameraLabel}
        </Typography>
        {siteLabel && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {siteLabel}
          </Typography>
        )}
      </TableCell>

      {/* Severity */}
      <TableCell>
        {event.severity ? (
          <SeverityChip severity={event.severity} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            --
          </Typography>
        )}
      </TableCell>

      {/* Detected -- relative time */}
      <TableCell>
        <Typography variant="body2" noWrap>
          {getTimeAgo(event.created_at)}
        </Typography>
      </TableCell>

      {/* Status */}
      <TableCell>
        <EventStatusBadge status={event.status} />
      </TableCell>
    </TableRow>
  );
});

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type SortDirection = 'asc' | 'desc';

export interface EventsTableProps {
  /** Events to render (already filtered by the parent tab) */
  events: VMSEvent[];
  /** Aggregate counts from the API (new / acknowledged / dismissed / promoted) */
  counts: EventCounts;
  /** True while the initial or filter-change load is in progress */
  loading: boolean;
  /** Error message when the fetch fails */
  error: string | null;
  /** Currently selected event IDs (checkbox state) */
  selectedEventIds: Set<number>;
  /** Called when the selection changes */
  onSelectionChange: (ids: Set<number>) => void;
  /** Called when a row is clicked (opens detail drawer) */
  onEventClick: (eventId: number) => void;
  /**
   * Called when a row is hovered with the camera_id of that event
   * (or null on mouse leave). Used for cross-section linking with
   * the Attention Feed to highlight the corresponding camera tile.
   */
  onRowHover?: (cameraId: number | null) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EventsTable({
  events,
  counts,
  loading,
  error,
  selectedEventIds,
  onSelectionChange,
  onEventClick,
  onRowHover,
}: EventsTableProps) {
  const paperRef = useRef<HTMLDivElement>(null);

  // -----------------------------------------------------------------------
  // Internal sort state (client-side, like CustomerList)
  // -----------------------------------------------------------------------

  const [order, setOrder] = useState<SortDirection>('desc');
  const [orderBy, setOrderBy] = useState<string>('created_at');

  const handleSort = (property: string) => () => {
    const isActive = orderBy === property;
    const newDir = isActive && order === 'asc' ? 'desc' : 'asc';
    setOrder(newDir);
    setOrderBy(property);
  };

  const sortedEvents = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      stableSort(events, getComparator(order, orderBy) as any) as VMSEvent[],
    [events, order, orderBy],
  );

  // -----------------------------------------------------------------------
  // Client-side infinite scroll (virtual pagination like CustomerList)
  // -----------------------------------------------------------------------

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Reset visible count when events or sort changes
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [sortedEvents, order, orderBy]);

  // Scroll-listener: walk up the DOM to find the scrollable ancestor
  useEffect(() => {
    const el = paperRef.current;
    if (!el) return;

    let scrollContainer: HTMLElement | null = el.parentElement;
    while (scrollContainer) {
      const { overflow, overflowY } = getComputedStyle(scrollContainer);
      if (
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowY === 'auto' ||
        overflowY === 'scroll'
      )
        break;
      scrollContainer = scrollContainer.parentElement;
    }
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer!;
      const nearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;

      if (nearBottom && !isLoadingMore && visibleCount < sortedEvents.length) {
        setIsLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, sortedEvents.length));
          setIsLoadingMore(false);
        }, 100);
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer!.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, visibleCount, sortedEvents.length]);

  const visibleRows = useMemo(
    () => sortedEvents.slice(0, visibleCount),
    [sortedEvents, visibleCount],
  );

  // -----------------------------------------------------------------------
  // Selection helpers
  // -----------------------------------------------------------------------

  const allSelected = events.length > 0 && selectedEventIds.size === events.length;
  const someSelected = selectedEventIds.size > 0 && selectedEventIds.size < events.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(events.map((e) => e.id)));
    }
  };

  const handleSelectRow = useCallback(
    (eventId: number) => {
      const next = new Set(selectedEventIds);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      onSelectionChange(next);
    },
    [selectedEventIds, onSelectionChange],
  );

  // -----------------------------------------------------------------------
  // "Showing X of Y" — total is the sum of counts from the API
  // -----------------------------------------------------------------------

  const totalCount = counts.new + counts.acknowledged + counts.dismissed + counts.promoted;

  // -----------------------------------------------------------------------
  // Render helpers
  // -----------------------------------------------------------------------

  const renderSkeletonRows = () =>
    Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => (
      <TableRow key={`skeleton-${idx}`}>
        <TableCell padding="checkbox">
          <Skeleton variant="rectangular" width={20} height={20} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="40%" sx={{ mt: 0.5 }} />
        </TableCell>
        <TableCell>
          <Skeleton variant="rounded" width={65} height={24} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="60%" />
        </TableCell>
        <TableCell>
          <Skeleton variant="rounded" width={90} height={24} />
        </TableCell>
      </TableRow>
    ));

  const renderEmptyState = () => (
    <TableRow>
      <TableCell colSpan={TOTAL_COLS} sx={{ border: 0 }}>
        <EmptyState
          icon={<EventBusyIcon />}
          title="No events found"
          message={
            error
              ? `Failed to load events: ${error}`
              : 'No events match the current filters. Try adjusting your search criteria.'
          }
          size="small"
        />
      </TableCell>
    </TableRow>
  );

  const renderEventRows = () =>
    visibleRows.map((event) => (
      <EventRow
        key={event.id}
        event={event}
        isSelected={selectedEventIds.has(event.id)}
        onEventClick={onEventClick}
        onSelectRow={handleSelectRow}
        onRowHover={onRowHover}
      />
    ));

  // -----------------------------------------------------------------------
  // Main render
  // -----------------------------------------------------------------------

  return (
    <Paper ref={paperRef} elevation={1}>
      {/* "Showing X of Y events" header -- exact Customers pattern */}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Showing {visibleRows.length} of {totalCount} events
        </Typography>
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 600 }}>
          {/* ---- Head ---- */}
          <TableHead>
            <TableRow>
              {eventsHeadTableCells.map((cell) => {
                // Checkbox column (property === '')
                if (!cell.property) {
                  return (
                    <TableCell key={cell.id} padding="checkbox">
                      <Checkbox
                        indeterminate={someSelected}
                        checked={allSelected}
                        onChange={handleSelectAll}
                        size="small"
                        inputProps={{ 'aria-label': 'Select all events' }}
                      />
                    </TableCell>
                  );
                }

                const isActive = orderBy === cell.property;
                const direction: SortDirection = isActive ? order : 'asc';

                return (
                  <TableCell
                    key={cell.id}
                    sortDirection={isActive ? order : false}
                  >
                    <TableSortLabel
                      active={isActive}
                      direction={direction}
                      onClick={handleSort(cell.property)}
                    >
                      {cell.label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          {/* ---- Body ---- */}
          <TableBody>
            {loading && events.length === 0
              ? renderSkeletonRows()
              : events.length === 0
                ? renderEmptyState()
                : renderEventRows()}

            {/* Loading indicator while scrolling for more */}
            {isLoadingMore && (
              <TableRow>
                <TableCell colSpan={TOTAL_COLS}>
                  <Box sx={{ width: '100%', py: 1 }}>
                    <LinearProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* End-of-list indicator */}
      <Box sx={{ p: 1, textAlign: 'center' }}>
        {!loading &&
          !isLoadingMore &&
          visibleRows.length >= sortedEvents.length &&
          sortedEvents.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              End of list
            </Typography>
          )}
      </Box>
    </Paper>
  );
}

export default EventsTable;
